
import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';

const PropertyLiensGuidePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Property Liens Explained: A Simple Guide for El Paso Homeowners | TREX RE LLC"
        description="Complete guide to property liens in Texas. Learn about tax liens, mechanic's liens, judgment liens, HOA liens, and how to sell a house with liens in El Paso. Understand your options and protect your property."
        keywords="property liens, tax liens El Paso, mechanic's lien Texas, judgment lien, HOA lien, sell house with liens, lien release, title issues El Paso"
        canonical="/elpaso-tx/property-liens-guide"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Property Liens Explained: A Simple Guide for El Paso Homeowners",
          "description": "Comprehensive guide covering all types of property liens, how they affect homeowners, and solutions for selling property with liens in Texas.",
          "author": {
            "@type": "Organization",
            "name": "TREX RE LLC"
          },
          "publisher": {
            "@type": "Organization",
            "name": "TREX RE LLC"
          }
        }}
      />
      <GlobalNavbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/f988f5c9d9eace9fe0cb45ed2d884cfc.jpg"
            alt="Property lien documents and legal paperwork"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-full mb-6">
              Property Liens Guide
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Property Liens Explained: A Simple Guide for El Paso Homeowners
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Discover everything you need to know about property liens—what they are, how they affect your home, and what options you have if you're dealing with liens on your El Paso property.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="/get-cash-offer-for-my-property"
                className="inline-flex items-center px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Get a Cash Offer Today
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
              <a
                href="tel:+1-915-206-5814"
                className="inline-flex items-center px-8 py-4 bg-white/10 backdrop-blur text-white font-semibold rounded-lg hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap border border-white/30"
              >
                <i className="ri-phone-line mr-2"></i>
                Call (915) 206-5814
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              If you own property in El Paso—or anywhere in Texas—understanding property liens is essential for protecting your investment and your financial future. A lien is a legal claim against your property that can prevent you from selling, refinancing, or even keeping your home if left unresolved. Yet many homeowners don't fully understand what liens are, how they work, or what to do when they discover one on their property.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Whether you've received a notice about a lien, discovered one during a title search, or simply want to educate yourself about potential risks, this comprehensive guide will walk you through everything you need to know. We'll cover the different types of liens, how they're created, their impact on property ownership, and most importantly—your options for resolving them.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Property liens affect thousands of El Paso homeowners every year. From <a href="/elpaso-tx/owing-too-much-property-tax" className="text-teal-600 hover:text-teal-700 font-semibold">unpaid property taxes</a> to contractor disputes, the reasons liens are placed on homes are varied and sometimes unexpected. The good news is that liens don't have to mean losing your home—there are solutions available, and understanding your options is the first step toward resolution.
            </p>
          </div>
        </div>
      </section>

      {/* What Is a Property Lien Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Exactly Is a <span className="text-teal-600">Property Lien</span>?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding the basics of liens and how they affect your property rights
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                A property lien is a legal claim or encumbrance placed on real estate by a creditor as security for a debt or obligation. In simple terms, it's a way for someone you owe money to—whether it's the government, a contractor, or a creditor—to ensure they get paid by attaching their claim to your property.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                When a lien is placed on your property, it becomes part of the public record. This means anyone who searches the title—such as potential buyers, lenders, or title companies—will see the lien. Until the lien is satisfied (paid off) or released, it "clouds" your title and can prevent you from selling or refinancing.
              </p>
              <div className="bg-teal-50 border-l-4 border-teal-500 p-6 rounded-r-lg">
                <h4 className="font-bold text-gray-900 mb-2">Key Point</h4>
                <p className="text-gray-700">
                  A lien doesn't mean you've lost your property—you still own it and can live in it. However, the lien gives the creditor a legal interest in your property that must be addressed before you can transfer clear ownership to someone else.
                </p>
              </div>
            </div>
            <div className="relative h-96 rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=homeowner%20reviewing%20property%20title%20documents%20with%20magnifying%20glass%20showing%20lien%20encumbrance%20on%20deed%20modern%20home%20office%20setting%20warm%20natural%20lighting%20concerned%20expression%20professional%20photography&width=800&height=600&seq=liens-guide-what-002&orientation=landscape"
                alt="Homeowner reviewing property title with lien"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="bg-white p-8 md:p-12 rounded-2xl shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">How Liens Work: The Basic Process</h3>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                  <span className="text-2xl font-bold text-teal-600">1</span>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Debt Occurs</h4>
                <p className="text-gray-600 text-sm">A debt or obligation is created—unpaid taxes, contractor work, court judgment, etc.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                  <span className="text-2xl font-bold text-teal-600">2</span>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Lien Filed</h4>
                <p className="text-gray-600 text-sm">The creditor files a lien with the county clerk, attaching it to your property's title.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                  <span className="text-2xl font-bold text-teal-600">3</span>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Title Clouded</h4>
                <p className="text-gray-600 text-sm">The lien appears on title searches, preventing clean transfer of ownership.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                  <span className="text-2xl font-bold text-teal-600">4</span>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Resolution</h4>
                <p className="text-gray-600 text-sm">Debt is paid, lien is released, and clear title is restored to the property.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Types of Liens Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Types of Property Liens <span className="text-teal-600">You Should Know</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Different liens have different origins, priorities, and implications for homeowners
            </p>
          </div>

          {/* Tax Liens */}
          <div className="mb-16">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center px-4 py-2 bg-red-100 text-red-700 font-semibold rounded-full mb-4">
                  Highest Priority
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Tax Liens</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Tax liens are among the most serious types of liens because they take priority over almost all other claims—including your mortgage. In Texas, both property taxes and federal income taxes can result in liens against your home.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  <strong>Property Tax Liens:</strong> When you fall behind on property taxes, the county automatically places a lien on your property. El Paso County has some of the <a href="/elpaso-tx/why-property-taxes-high-el-paso" className="text-teal-600 hover:text-teal-700 font-semibold">highest property tax rates in Texas</a>, making this a common issue for local homeowners. If unpaid, the county can eventually <a href="/elpaso-tx/el-paso-tax-foreclosure-information-deadlines" className="text-teal-600 hover:text-teal-700 font-semibold">foreclose on your property through a tax sale</a>.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  <strong>Federal Tax Liens:</strong> If you owe back taxes to the IRS, they can file a federal tax lien against all your property, including real estate. These liens are particularly aggressive and can affect your credit score significantly.
                </p>
                <div className="bg-red-50 p-6 rounded-lg">
                  <h4 className="font-bold text-gray-900 mb-2">Why Tax Liens Are Dangerous</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-alert-line text-red-600 mt-1 mr-2"></i>
                      <span>They take priority over your mortgage</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-alert-line text-red-600 mt-1 mr-2"></i>
                      <span>Interest and penalties accumulate rapidly</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-alert-line text-red-600 mt-1 mr-2"></i>
                      <span>Can lead to forced sale of your property</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="relative h-96 rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=property%20tax%20bill%20notice%20with%20past%20due%20stamp%20and%20penalty%20charges%20on%20kitchen%20table%20with%20calculator%20and%20worried%20homeowner%20hands%20visible%20el%20paso%20texas%20home%20setting%20warm%20natural%20lighting%20professional%20photography&width=800&height=600&seq=liens-guide-tax-003&orientation=landscape"
                  alt="Property tax lien notice"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          {/* Mechanic's Liens */}
          <div className="mb-16">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1 relative h-96 rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=contractor%20construction%20worker%20reviewing%20unpaid%20invoice%20paperwork%20at%20residential%20home%20renovation%20site%20tools%20and%20building%20materials%20visible%20el%20paso%20texas%20southwestern%20style%20home%20warm%20afternoon%20lighting%20professional%20photography&width=800&height=600&seq=liens-guide-mechanic-004&orientation=landscape"
                  alt="Contractor with unpaid invoice"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="order-1 md:order-2">
                <div className="inline-flex items-center px-4 py-2 bg-amber-100 text-amber-700 font-semibold rounded-full mb-4">
                  Construction Related
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Mechanic's Liens (Construction Liens)</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Mechanic's liens protect contractors, subcontractors, and suppliers who provide labor or materials for property improvements. If they aren't paid for their work, Texas law allows them to file a lien against your property—even if you paid the general contractor who failed to pay them.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  This is particularly concerning because you might think all bills are paid, only to discover months later that a subcontractor filed a lien. If you've had work done on a property that <a href="/elpaso-tx/cant-afford-repairs-to-sell-house" className="text-teal-600 hover:text-teal-700 font-semibold">needed repairs</a>, it's crucial to verify all parties were paid.
                </p>
                <div className="bg-amber-50 p-6 rounded-lg">
                  <h4 className="font-bold text-gray-900 mb-2">Texas Mechanic's Lien Rules</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-information-line text-amber-600 mt-1 mr-2"></i>
                      <span>Must be filed within specific deadlines (varies by claimant type)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-information-line text-amber-600 mt-1 mr-2"></i>
                      <span>Requires proper notice to property owner</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-information-line text-amber-600 mt-1 mr-2"></i>
                      <span>Can be filed by contractors, subcontractors, and material suppliers</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Judgment Liens */}
          <div className="mb-16">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center px-4 py-2 bg-purple-100 text-purple-700 font-semibold rounded-full mb-4">
                  Court Ordered
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Judgment Liens</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  When someone wins a lawsuit against you and obtains a money judgment, they can file that judgment as a lien against your property. This includes judgments from credit card lawsuits, personal injury cases, breach of contract disputes, and other civil matters.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Judgment liens in Texas are valid for 10 years and can be renewed. They attach to all real property you own in the county where filed, and creditors can file in multiple counties. These liens can be particularly problematic for <a href="/elpaso-tx/inherited-home-dont-want-it" className="text-teal-600 hover:text-teal-700 font-semibold">inherited properties</a> where the deceased had outstanding judgments.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  The good news is that Texas has strong <a href="/elpaso-tx/texas-homestead-exemptions-protections" className="text-teal-600 hover:text-teal-700 font-semibold">homestead protections</a> that prevent most judgment creditors from forcing the sale of your primary residence. However, the lien still clouds your title and must be addressed when you sell.
                </p>
              </div>
              <div className="relative h-96 rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=court%20judgment%20document%20with%20official%20seal%20and%20gavel%20on%20judge%20desk%20legal%20papers%20showing%20money%20judgment%20amount%20texas%20courthouse%20setting%20professional%20legal%20photography%20warm%20lighting&width=800&height=600&seq=liens-guide-judgment-005&orientation=landscape"
                  alt="Court judgment lien document"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          {/* Other Lien Types Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
                <i className="ri-community-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">HOA Liens</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Homeowners associations can place liens for unpaid dues, special assessments, and fines. In Texas, HOAs have significant power and can even foreclose on properties for unpaid assessments—sometimes for relatively small amounts that balloon with fees and interest.
              </p>
              <p className="text-gray-600 leading-relaxed">
                If you're dealing with <a href="/rental-property" className="text-teal-600 hover:text-teal-700 font-semibold">rental properties</a> in HOA communities, staying current on dues is essential to avoid these liens.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
                <i className="ri-parent-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Child Support Liens</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                The Texas Attorney General's office can place liens on property for unpaid child support. These liens are automatically filed when child support arrears reach certain thresholds and can prevent property sales until the debt is satisfied.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Child support liens follow the property, meaning if you <a href="/inherited-homes" className="text-teal-600 hover:text-teal-700 font-semibold">inherit a home</a> from someone who owed child support, you may need to address this lien.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
                <i className="ri-hospital-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Medicaid Estate Recovery Liens</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                When someone receives Medicaid benefits and passes away, the state can file a claim against their estate to recover costs. This creates a lien that must be satisfied before heirs can receive clear title to inherited property.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Understanding <a href="/elpaso-tx/texas-probate-guide" className="text-teal-600 hover:text-teal-700 font-semibold">Texas probate</a> is essential when dealing with Medicaid recovery claims on inherited estates.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
                <i className="ri-bank-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Mortgage Liens</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Your mortgage is actually a voluntary lien you agreed to when you bought your home. It gives the lender the right to foreclose if you don't make payments. If you're <a href="/elpaso-tx/cant-pay-mortgage" className="text-teal-600 hover:text-teal-700 font-semibold">struggling to pay your mortgage</a>, understanding your options is crucial.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Second mortgages, HELOCs, and home equity loans also create liens that must be paid when you sell.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
                <i className="ri-tools-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Municipal Liens</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Cities can place liens for unpaid utility bills, code violation fines, demolition costs, or weed abatement. These municipal liens can accumulate quickly, especially on vacant or <a href="/elpaso-tx/sell-hoarder-houses" className="text-teal-600 hover:text-teal-700 font-semibold">neglected properties</a>.
              </p>
              <p className="text-gray-600 leading-relaxed">
                El Paso actively enforces code violations and can place substantial liens on properties that don't comply with local ordinances.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
                <i className="ri-file-list-3-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Lis Pendens</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                While not technically a lien, a lis pendens ("litigation pending") is a notice filed in public records indicating that a lawsuit affecting the property is ongoing. It warns potential buyers that the property's ownership is in dispute.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Lis pendens are common in <a href="/avoid-foreclosure" className="text-teal-600 hover:text-teal-700 font-semibold">foreclosure proceedings</a>, divorce cases, and boundary disputes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lien Priority Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Understanding <span className="text-teal-600">Lien Priority</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Not all liens are created equal—priority determines who gets paid first
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                When a property is sold or foreclosed upon, liens are paid in order of priority. Understanding lien priority is crucial because if there isn't enough money to pay all liens, lower-priority liens may go unpaid.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Generally, the rule is "first in time, first in right"—meaning liens are prioritized by when they were recorded. However, there are important exceptions:
              </p>
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <div className="flex items-center mb-2">
                    <span className="w-8 h-8 flex items-center justify-center bg-red-100 text-red-600 font-bold rounded-full mr-3">1</span>
                    <h4 className="font-bold text-gray-900">Property Tax Liens</h4>
                  </div>
                  <p className="text-gray-600 ml-11">Always first priority, regardless of when filed</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <div className="flex items-center mb-2">
                    <span className="w-8 h-8 flex items-center justify-center bg-amber-100 text-amber-600 font-bold rounded-full mr-3">2</span>
                    <h4 className="font-bold text-gray-900">First Mortgage</h4>
                  </div>
                  <p className="text-gray-600 ml-11">Typically second priority after tax liens</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <div className="flex items-center mb-2">
                    <span className="w-8 h-8 flex items-center justify-center bg-teal-100 text-teal-600 font-bold rounded-full mr-3">3</span>
                    <h4 className="font-bold text-gray-900">Other Liens</h4>
                  </div>
                  <p className="text-gray-600 ml-11">Mechanic's liens, judgments, HOA liens—by recording date</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Example: Lien Priority in Action</h3>
              <p className="text-gray-600 mb-6">
                Imagine a home sells for $200,000 with the following liens:
              </p>
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <span className="text-gray-700">Property Tax Lien</span>
                  <span className="font-bold text-gray-900">$15,000</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <span className="text-gray-700">First Mortgage</span>
                  <span className="font-bold text-gray-900">$150,000</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <span className="text-gray-700">Judgment Lien</span>
                  <span className="font-bold text-gray-900">$25,000</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <span className="text-gray-700">Mechanic's Lien</span>
                  <span className="font-bold text-gray-900">$20,000</span>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-4">
                <p className="text-gray-600 mb-2"><strong>Total Liens:</strong> $210,000</p>
                <p className="text-gray-600 mb-2"><strong>Sale Price:</strong> $200,000</p>
                <p className="text-gray-600"><strong>Result:</strong> Tax lien and mortgage paid in full. Judgment lien receives $35,000 (partial). Mechanic's lien receives nothing.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Discover Liens Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How to Discover Liens <span className="text-teal-600">on Your Property</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Finding out what liens exist is the first step toward resolution
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
            <div className="relative h-80 rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=title%20company%20professional%20reviewing%20property%20title%20search%20documents%20on%20computer%20screen%20showing%20lien%20records%20modern%20office%20setting%20el%20paso%20texas%20warm%20professional%20lighting&width=800&height=500&seq=liens-guide-discover-006&orientation=landscape"
                alt="Title search for property liens"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Methods to Find Liens</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg mr-4 flex-shrink-0">
                    <i className="ri-search-line text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Title Search</h4>
                    <p className="text-gray-600">The most comprehensive method. A title company searches public records for all liens, encumbrances, and claims against your property.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg mr-4 flex-shrink-0">
                    <i className="ri-building-line text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">County Clerk's Office</h4>
                    <p className="text-gray-600">You can search records yourself at the El Paso County Clerk's office or their online portal for recorded liens and judgments.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg mr-4 flex-shrink-0">
                    <i className="ri-government-line text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Tax Assessor's Office</h4>
                    <p className="text-gray-600">Check with <a href="/elpaso-tx/epcad" className="text-teal-600 hover:text-teal-700 font-semibold">EPCAD</a> and the county tax office for property tax liens and delinquencies.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg mr-4 flex-shrink-0">
                    <i className="ri-file-search-line text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Online Databases</h4>
                    <p className="text-gray-600">Various online services offer lien searches, though they may not be as comprehensive as a professional title search.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-8 rounded-r-lg">
            <h4 className="font-bold text-gray-900 mb-3">Pro Tip: Get a Title Search Before Listing</h4>
            <p className="text-gray-700 leading-relaxed">
              If you're planning to sell your home, order a preliminary title search before listing. This gives you time to address any liens or <a href="/title-issues" className="text-teal-600 hover:text-teal-700 font-semibold">title issues</a> before they derail your sale. Many sellers are surprised to discover liens they didn't know existed—from old judgments to mechanic's liens filed by subcontractors years ago.
            </p>
          </div>
        </div>
      </section>

      {/* How to Remove Liens Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How to Remove or Resolve <span className="text-teal-600">Property Liens</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Several strategies exist for dealing with liens on your property
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
                <i className="ri-money-dollar-circle-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Pay the Debt</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                The most straightforward solution is to pay the underlying debt in full. Once paid, the creditor must file a lien release with the county, clearing your title.
              </p>
              <p className="text-gray-600 leading-relaxed">
                For property tax liens, you may be able to set up <a href="/elpaso-tx/property-tax-payment-plans-deferral" className="text-teal-600 hover:text-teal-700 font-semibold">payment plans or deferrals</a> to make the debt more manageable.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
                <i className="ri-discuss-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Negotiate a Settlement</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Many creditors will accept less than the full amount owed, especially if the alternative is getting nothing. This is particularly common with old judgment liens or when the property has limited equity.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Experienced <a href="/elpaso-tx/cash-home-buyers-el-paso" className="text-teal-600 hover:text-teal-700 font-semibold">cash home buyers</a> often have relationships with lien holders and can negotiate better settlements than individual homeowners.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
                <i className="ri-scales-3-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Dispute the Lien</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                If you believe a lien was filed improperly or the debt isn't valid, you can challenge it in court. This requires proving the lien doesn't meet legal requirements or that the underlying debt is invalid.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Mechanic's liens, in particular, have strict procedural requirements that, if not followed, can invalidate the lien.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
                <i className="ri-time-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Wait for Expiration</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Some liens have expiration dates. Judgment liens in Texas expire after 10 years if not renewed. Mechanic's liens must be enforced within specific timeframes or they become unenforceable.
              </p>
              <p className="text-gray-600 leading-relaxed">
                However, waiting isn't always practical if you need to sell or refinance your property.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
                <i className="ri-shield-check-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">File for Bankruptcy</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                In some cases, bankruptcy can eliminate or reduce certain liens. Chapter 7 can discharge the underlying debt, and Chapter 13 can help you catch up on secured debts like mortgages and property taxes.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Bankruptcy is a serious decision with long-term consequences—consult with a bankruptcy attorney before proceeding.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
                <i className="ri-home-heart-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Sell to a Cash Buyer</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Cash buyers who specialize in properties with liens can purchase your home and handle lien resolution as part of the transaction. Liens are paid from the sale proceeds at closing.
              </p>
              <p className="text-gray-600 leading-relaxed">
                This is often the fastest and easiest solution, especially when dealing with multiple liens or complex situations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Selling a House with Liens Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Selling a House with Liens: <span className="text-teal-600">Your Options</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Having liens doesn't mean you can't sell—it just requires the right approach
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Can You Sell a House with Liens?</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Yes, you can sell a house with liens—but the liens must be addressed at or before closing. In most cases, liens are paid from the sale proceeds through the title company. The title company ensures all liens are satisfied before transferring clear title to the buyer.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                However, if the liens exceed your home's value, you have a problem. You'll either need to bring money to closing to pay off the excess, negotiate with lien holders to accept less, or explore alternatives like a <a href="/elpaso-tx/short-sale-pros-cons-underwater-homeowners" className="text-teal-600 hover:text-teal-700 font-semibold">short sale</a>.
              </p>
              <div className="bg-teal-50 p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-3">When Liens Exceed Property Value</h4>
                <p className="text-gray-700">
                  If you owe more than your home is worth (including liens), you may need to negotiate with creditors or consider a <a href="/elpaso-tx/deed-in-lieu-of-foreclosure" className="text-teal-600 hover:text-teal-700 font-semibold">deed-in-lieu of foreclosure</a>. A <a href="/elpaso-tx/loan-modification-options" className="text-teal-600 hover:text-teal-700 font-semibold">loan modification</a> might also help if you want to keep the property.
                </p>
              </div>
            </div>
            <div className="relative h-96 rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=happy%20homeowner%20shaking%20hands%20with%20cash%20buyer%20in%20front%20of%20southwestern%20style%20home%20successful%20property%20sale%20despite%20liens%20el%20paso%20texas%20desert%20landscape%20warm%20golden%20hour%20lighting%20professional%20real%20estate%20photography&width=800&height=600&seq=liens-guide-sell-007&orientation=landscape"
                alt="Selling house with liens to cash buyer"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="bg-gray-900 text-white p-8 md:p-12 rounded-2xl">
            <h3 className="text-2xl font-bold mb-8 text-center">Traditional Sale vs. Cash Buyer: Handling Liens</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-xl font-bold text-red-400 mb-4">Traditional Sale Challenges</h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <i className="ri-close-circle-line text-red-400 mt-1 mr-3"></i>
                    <span>Buyers may walk away when liens are discovered</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-circle-line text-red-400 mt-1 mr-3"></i>
                    <span>Lenders won't approve mortgages on clouded titles</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-circle-line text-red-400 mt-1 mr-3"></i>
                    <span>You must resolve liens before listing or risk failed sales</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-circle-line text-red-400 mt-1 mr-3"></i>
                    <span>Negotiating with multiple creditors is time-consuming</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-circle-line text-red-400 mt-1 mr-3"></i>
                    <span>May need to pay liens out of pocket before selling</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="text-xl font-bold text-teal-400 mb-4">Cash Buyer Advantages</h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-400 mt-1 mr-3"></i>
                    <span>We buy houses with liens—no surprises</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-400 mt-1 mr-3"></i>
                    <span>No bank approval needed—we pay cash</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-400 mt-1 mr-3"></i>
                    <span>We handle all lien negotiations and payoffs</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-400 mt-1 mr-3"></i>
                    <span>Close in 7-14 days, even with complex liens</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-400 mt-1 mr-3"></i>
                    <span>Liens paid from proceeds—no out-of-pocket costs</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Preventing Liens Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How to Prevent Liens <span className="text-teal-600">on Your Property</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Proactive steps to protect your property from future liens
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <i className="ri-calendar-check-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Pay Taxes on Time</h3>
              <p className="text-gray-600 text-sm">
                Property taxes are due January 31st in Texas. Set up automatic payments or escrow through your mortgage to avoid delinquency.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <i className="ri-file-list-2-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Get Lien Waivers</h3>
              <p className="text-gray-600 text-sm">
                When hiring contractors, require lien waivers from all subcontractors and suppliers before making final payment.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <i className="ri-mail-open-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Address Debts Promptly</h3>
              <p className="text-gray-600 text-sm">
                Don't ignore collection notices or lawsuits. Addressing debts early can prevent them from becoming liens.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <i className="ri-search-eye-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Monitor Your Title</h3>
              <p className="text-gray-600 text-sm">
                Periodically check public records for any liens filed against your property. Early detection allows faster resolution.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked <span className="text-teal-600">Questions About Liens</span>
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about property liens answered
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-gray-50 p-8 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Can someone put a lien on my house without me knowing?</h3>
              <p className="text-gray-600 leading-relaxed">
                Yes, in many cases liens can be filed without direct notification to you. While some liens require notice (like mechanic's liens), others—such as judgment liens or tax liens—may be filed based on public court records or tax delinquencies. This is why periodic title searches are important, especially if you've had legal or financial issues.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-3">How long does a lien stay on my property in Texas?</h3>
              <p className="text-gray-600 leading-relaxed">
                It depends on the type of lien. Property tax liens remain until paid. Judgment liens last 10 years but can be renewed. Mechanic's liens must be enforced within specific timeframes (typically 1-2 years) or they expire. Federal tax liens last 10 years from assessment. The lien remains on your title until it's either paid, released, or expires.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Can I refinance my house if there's a lien on it?</h3>
              <p className="text-gray-600 leading-relaxed">
                Generally, no. Lenders require clear title before approving a refinance. The lien would need to be paid off or subordinated (moved to a lower priority position) before refinancing. Some lenders may allow refinancing if the new loan pays off the existing lien, but this depends on the lien type and amount.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-3">What happens if I inherit a house with liens?</h3>
              <p className="text-gray-600 leading-relaxed">
                Liens transfer with the property, so you inherit them along with the house. You're not personally liable for the deceased's debts, but the liens must be satisfied before you can sell with clear title. If the liens exceed the property's value, you may need to negotiate with creditors or consider disclaiming the inheritance. Understanding <a href="/elpaso-tx/texas-intestacy-laws" className="text-teal-600 hover:text-teal-700 font-semibold">Texas inheritance laws</a> can help navigate this situation.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Can a lien holder force the sale of my home?</h3>
              <p className="text-gray-600 leading-relaxed">
                It depends on the lien type and Texas law. Property tax authorities can foreclose and sell your home for unpaid taxes. Mortgage lenders can foreclose for non-payment. However, most judgment creditors cannot force the sale of your homestead in Texas due to strong homestead protections. HOAs can foreclose for unpaid assessments in some circumstances.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-3">How do I get a lien release after paying off the debt?</h3>
              <p className="text-gray-600 leading-relaxed">
                Once you pay the debt, the creditor is legally required to file a lien release with the county clerk's office. Request a release in writing and follow up to ensure it's filed. If the creditor fails to file a release within a reasonable time (30 days in Texas for most liens), you may have legal recourse. Keep all payment receipts as proof.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-3">What's the difference between a lien and a judgment?</h3>
              <p className="text-gray-600 leading-relaxed">
                A judgment is a court decision that you owe money to someone. A lien is a legal claim against specific property. When a creditor wins a judgment, they can then file it as a lien against your real property, converting the general debt into a secured claim against your home. Not all judgments become liens—the creditor must take the additional step of recording the judgment.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Can I sell my house if I owe more in liens than it's worth?</h3>
              <p className="text-gray-600 leading-relaxed">
                Yes, but it's more complicated. You'll need to either bring money to closing to cover the shortfall, negotiate with lien holders to accept less (a short sale), or work with a cash buyer experienced in these situations. Some creditors will accept partial payment rather than risk getting nothing if you can't sell. An experienced <a href="/elpaso-tx/sell-house-fast" className="text-teal-600 hover:text-teal-700 font-semibold">cash home buyer</a> can help navigate these negotiations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Resources Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Related <span className="text-teal-600">Resources</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore more guides to help with your property situation
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <a href="/title-issues" className="group bg-white rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300">
              <div className="relative h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=property%20title%20documents%20with%20magnifying%20glass%20showing%20title%20issues%20and%20encumbrances%20professional%20legal%20setting%20warm%20lighting&width=600&height=400&seq=liens-resource-008&orientation=landscape"
                  alt="Title issues guide"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">Selling Houses with Title Issues</h3>
                <p className="text-gray-600 text-sm">Complete guide to resolving title problems</p>
              </div>
            </a>

            <a href="/elpaso-tx/owing-too-much-property-tax" className="group bg-white rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300">
              <div className="relative h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=property%20tax%20bills%20and%20delinquent%20notices%20on%20desk%20with%20calculator%20showing%20tax%20debt%20stress%20warm%20natural%20lighting&width=600&height=400&seq=liens-resource-009&orientation=landscape"
                  alt="Property tax debt guide"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">Owing Too Much Property Tax</h3>
                <p className="text-gray-600 text-sm">Solutions for property tax debt</p>
              </div>
            </a>

            <a href="/avoid-foreclosure" className="group bg-white rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300">
              <div className="relative h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=foreclosure%20notice%20document%20on%20front%20door%20of%20home%20showing%20financial%20hardship%20warm%20afternoon%20lighting&width=600&height=400&seq=liens-resource-010&orientation=landscape"
                  alt="Avoid foreclosure guide"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">Avoid Foreclosure</h3>
                <p className="text-gray-600 text-sm">Options to save your home</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Dealing with Liens on Your El Paso Property?
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Don't let liens keep you trapped in a property you need to sell. We buy houses with liens and handle all the complications for you. Get a fair cash offer and close on your timeline—liens paid from proceeds, no out-of-pocket costs.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a
              href="/get-cash-offer-for-my-property"
              className="inline-flex items-center px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Get Your Cash Offer
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
            <a
              href="tel:+1-915-206-5814"
              className="inline-flex items-center px-8 py-4 bg-white/10 backdrop-blur text-white font-semibold rounded-lg hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap border border-white/30"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (915) 206-5814
            </a>
          </div>
          <p className="text-gray-400 text-sm">
            No pressure. No obligation. We'll explain all your options—even if selling to us isn't the best one.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PropertyLiensGuidePage;
